import * as React from 'react';
import { useSelector } from 'react-redux';
import Carousel from '../carousel';
import CustomLink from '../custom-link';
import TileCarouselProps from './tile-carousel.types.d';
import { cn, get, getText } from '../../../helpers';
import DataTile from '../../tile/dataset-tile';
import SolutionTile from '../../tile/solution-tile';
import BlueprintTile from '../../tile/blueprint-tile/blueprint-tile';
import QueryTile from '../../query';

export default function TileCarousel(props: TileCarouselProps.TileCarouselProps) {
    const { dataIds, slickOptions, title, showMoreLink, className, fullBleed, queryString, showMoreBlueprints, newTab, source, carouselCompKey, qlProfileId, queryLibraryRelatedQueries, showMoreQueries, associatedDatasets, showTrendingQueries, trendingQueriesList } = props;

    const [openSearchDatasets, setOpenSearchDatasets] = React.useState<DataProfileCard[]>([]);
    const [allSolutions, setAllSolutions] = React.useState<APISolution[]>([]);
    const [allBlueprints, setAllBlueprints] = React.useState<APIBlueprint[]>([]);
    const [relatedQueries, setRelatedQueries] = React.useState<QlRelatedQueries[]>([]);
    const [queryLibraryProfile, setQueryLibraryProfile] = React.useState<QueryLibraryProfile | null>(null);
    const [isMobile, setIsMobile] = React.useState(false);
    const [isDesktop, setIsDesktop] = React.useState(false);

    // const isUserLoggedIn = useSelector((state: RootStoreState) => get(state, 'user.isLoggedIn', false));
    const isUserLoggedIn = true;
    const { isLoggedIn, bookmarkPreference } = useSelector((state: RootStoreState) => get(state, 'user'));
    let bookmark: Bookmarks = { datasets: [], solutions: [], queries: [], blueprints: [] };
    if (bookmarkPreference) bookmark = bookmarkPreference.bookMarks || { datasets: [], solutions: [], queries: [], blueprints: [] };
    const hasMoreThan18Queries = queryLibraryRelatedQueries && queryLibraryRelatedQueries.HasMoreThan18Queries;
    const viewAllQueriesText = getText('generic.viewAllQueries');
    const query1: Query = {
        name: viewAllQueriesText,
        queryId: 0,
        datasets: [],
        associatedDatasets,
    };

    let titleClassName = 'tile-carousel__title';
    if (showTrendingQueries) {
        titleClassName = 'tile-carousel__trendingtitle_ql';
    } else if (showMoreQueries) {
        titleClassName = 'tile-carousel__title_ql';
    }

    const opensearchdatasets = useSelector((state: RootStoreState) => {
        if (dataIds.some(x => typeof x === 'number')) {
            return get(state, 'datasets.openSearch.response', []);
        }
        return [];
    });

    const allsolutions = useSelector((state: RootStoreState) => {
        if (dataIds.some(x => typeof x !== 'number')) {
            return get(state, 'solutions.openSearch.response', []);
        }
        return [];
    });

    const allblueprints = useSelector((state: RootStoreState) => {
        if (dataIds.some(x => typeof x !== 'number')) {
            return get(state, 'blueprints.openSearch.response', []);
        }
        return [];
    });

    const relatedqueries = useSelector((state: RootStoreState) => {
        if (dataIds.some(x => typeof x === 'number')) {
            return get(state, `querylibrary.relatedQueries.${qlProfileId}.response`, []);
        }
        return [];
    });

    React.useEffect(() => {
        const handleResize = () => {
            const mobile = window.matchMedia('(min-width: 728px)').matches;
            const desktop = window.matchMedia('(min-width: 1130px)').matches;

            setIsMobile(mobile);
            setIsDesktop(desktop);
        };

        handleResize();
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    React.useEffect(() => {
        if (opensearchdatasets.length > 0) setOpenSearchDatasets(opensearchdatasets);
        if (allsolutions.length > 0) setAllSolutions(allsolutions);
        if (allblueprints.length > 0) setAllBlueprints(allblueprints);
        if (relatedqueries.length > 0) setRelatedQueries(relatedqueries);
    }, [opensearchdatasets, allsolutions, allblueprints, relatedqueries]);

    const url = `/datasets${queryString || ''}`;
    const blueprintsUrl = '/blueprints';

    const reformat = (array: number[]) => {
        const result: number[] = [];
        const halfLength = 3;

        for (let i = 0; i < halfLength; i++) {
            result.push(array[i]);
            result.push(array[i + halfLength]);
        }
        return result;
    };

    const getSequenceDatasets = () => {
        const dataTiles: JSX.Element[] = [];
        let finalArray: (string | number| undefined) [] = [];
        if (showMoreQueries || showTrendingQueries) {
            if (dataIds) {
                if (isDesktop) {
                    for (let i = 0; i < dataIds.length; i += 6) {
                        const ids = dataIds.slice(i, i + 6);
                        if (i !== 0 && ids.length !== 6 && hasMoreThan18Queries) {
                            ids.push(0);
                        }
                        const reformatIds = reformat(ids as number[]);
                        finalArray = [...finalArray, ...reformatIds];
                    }
                } else {
                    finalArray = [...dataIds];
                    if (hasMoreThan18Queries) {
                        finalArray.push(0);
                    } else if (finalArray.length % 2 !== 0) {
                        finalArray.push(undefined);
                    }
                }
            }
        }

        const updatedDataIds = showMoreQueries || showTrendingQueries ? finalArray : dataIds;
        updatedDataIds.forEach(dataId => {
            if (typeof dataId === 'string') {
                if (showMoreBlueprints) {
                    const tile = allBlueprints.find((item: any) => item.KeyMarketplaceBlueprint === dataId);
                    if (tile) {
                        dataTiles.push(<BlueprintTile key={tile.KeyMarketplaceBlueprint} dataProfile={tile} />);
                    }
                } else {
                    const tile = allSolutions.find((item: APISolution) => item.KeyPlatformService === dataId);
                    if (tile) dataTiles.push(<SolutionTile key={tile.KeyPlatformService} solution={tile} newTab={newTab} />);
                    const blueprintTile = allBlueprints.find((item: any) => item.KeyMarketplaceBlueprint === dataId);
                    if (blueprintTile) dataTiles.push(<BlueprintTile key={blueprintTile.KeyMarketplaceBlueprint} dataProfile={blueprintTile} newTab={newTab} />);
                }
            } else if (showMoreQueries === true) {
                const querytile = queryLibraryRelatedQueries && queryLibraryRelatedQueries.RelatedQueries && queryLibraryRelatedQueries.RelatedQueries.length > 0 && queryLibraryRelatedQueries.RelatedQueries.find((item: RelatedQueries) => item.KeyMarketplaceQueryRelated === dataId);
                if (querytile) {
                    const query = {
                        ...querytile,
                        queryId: querytile.KeyMarketplaceQueryRelated,
                        name: querytile.QueryNameRelated,
                        datasets: querytile.AssociatedDatasets.map((dataset: AssociatedDataset) => dataset.DatasetName),
                        versiondate: querytile.QueryCurrentVerDate
                    };
                    dataTiles.push(<QueryTile key={query.queryId} query={query} isLoggedIn={isLoggedIn} bookmarks={bookmark} page="profile" />);
                } else if (dataId === 0) {
                    dataTiles.push(<QueryTile key={query1.queryId} query={query1} isLoggedIn bookmarks={bookmark} page="profile" />);
                } else if (dataId === undefined && isMobile) {
                    dataTiles.push(<> </>);
                }
            } else if (showTrendingQueries === true) {
                const trendingQueryTile = trendingQueriesList && trendingQueriesList.length > 0 && trendingQueriesList.find((item: QueryLibraryTrendingQueries) => item.KeyMarketplaceQuery === dataId);
                if (trendingQueryTile) {
                    const query = {
                        ...trendingQueryTile,
                        queryId: trendingQueryTile.KeyMarketplaceQuery,
                        name: trendingQueryTile.MarketplaceQueryTitle,
                        datasets: [trendingQueryTile.MarketplaceDatasetName],
                        versiondate: trendingQueryTile.MarketplaceQueryCurrentVerDate
                    };
                    dataTiles.push(<QueryTile key={query.queryId} query={query} isLoggedIn={isLoggedIn} bookmarks={bookmark} page="profile" />);
                }
            } else {
                const tile = openSearchDatasets.find((item: DataProfileCard) => item.id === dataId);
                if (tile) dataTiles.push(<DataTile key={tile.id} dataProfile={tile} newTab={newTab} />);
            }
        });
        return dataTiles;
    };

    const filteredData = getSequenceDatasets();

    return filteredData.length ? (
        <section className={cn('tile-carousel', { 'tile-carousel--full-bleed': fullBleed }, 'page-section', className)}>
            <div className={source && source === 'genai' ? 'page-section__inner__genai' : 'page-section__inner'}>
                {title && <h4 className={titleClassName}>{title}</h4>}
                <Carousel carouselSettings={slickOptions} carouselCompKey={carouselCompKey} showMoreQueries={showMoreQueries} records={dataIds} showTrendingQueries={showTrendingQueries}>{filteredData}</Carousel>
                {showMoreLink && !showMoreBlueprints && (
                    <CustomLink
                        className={
                            slickOptions && slickOptions.slidesToShow && filteredData.length > slickOptions.slidesToShow ? 'slick-slider-links' : ''
                        }
                        url={url}
                        text={getText('generic.seeAllDatasets')}
                        linkTheme="alternative"
                        hasIcon
                        isUpperCase
                    />
                )}
                {!showMoreLink && showMoreBlueprints && (
                    <CustomLink
                        className={
                            slickOptions && slickOptions.slidesToShow && filteredData.length > slickOptions.slidesToShow ? 'slick-slider-links' : ''
                        }
                        url={blueprintsUrl}
                        text={getText('generic.seeAllBlueprints')}
                        linkTheme="alternative"
                        hasIcon
                        isUpperCase
                    />
                )}
            </div>
        </section>
    ) : null;
}
